import React, { useEffect, useRef } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';

interface AuthGuardProps {
  children: React.ReactNode;
  requireAuth?: boolean;
  allowGuest?: boolean;
  requireAdmin?: boolean;
}

const AuthGuard: React.FC<AuthGuardProps> = ({ 
  children, 
  requireAuth = true, 
  allowGuest = false,
  requireAdmin = false
}) => {
  const auth = useAuth();
  const location = useLocation();

  // Use ref to prevent multiple validation calls
  const hasValidated = useRef(false);

  // Validate authentication state on component mount (once)
  useEffect(() => {
    if (!hasValidated.current && auth.isAuthenticated && !auth.isSessionValid()) {
      console.log('[AuthGuard] Invalid session detected');
      auth.logout();
      hasValidated.current = true;
    }
  }, [auth.isAuthenticated, auth.isSessionValid]);

  const needsAuthenticatedSession = requireAdmin || (requireAuth && !allowGuest);

  if (needsAuthenticatedSession && !auth.isAuthenticated) {
    // Redirect to login page with return url
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (requireAuth && auth.isGuest && !allowGuest) {
    // Guest is authenticated but route doesn't allow guest access
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (requireAdmin && (!auth.user || auth.user.type !== 'ADMIN')) {
    // User is not admin, redirect to home or unauthorized page
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

export default AuthGuard;
